import { useEffect, useRef, useState } from "react";

type Print = { id: number; x: number; y: number; angle: number };

// Per-viewer on/off preference (each browser remembers its own choice).
const PAW_KEY = "resq_paw_trail";
export function isPawEnabled(): boolean {
  try { return localStorage.getItem(PAW_KEY) !== "off"; } catch { return true; }
}
export function setPawEnabled(on: boolean): void {
  try { localStorage.setItem(PAW_KEY, on ? "on" : "off"); } catch { /* ignore */ }
  window.dispatchEvent(new Event("pawtrail:changed"));
}

/** A wildlife-friendly cursor trail: moving the pointer leaves paw-prints
 *  (alternating left/right, turned to face the direction of travel) that fade
 *  out. Purely decorative — never blocks clicks, respects reduced-motion, and
 *  can be switched off per viewer. */
export function PawTrail() {
  const [prints, setPrints] = useState<Print[]>([]);
  const [enabled, setEnabled] = useState(isPawEnabled());
  const last = useRef<{ x: number; y: number } | null>(null);
  const foot = useRef<1 | -1>(1);
  const idRef = useRef(0);

  useEffect(() => {
    const onChange = () => setEnabled(isPawEnabled());
    window.addEventListener("pawtrail:changed", onChange);
    return () => window.removeEventListener("pawtrail:changed", onChange);
  }, []);

  useEffect(() => {
    if (!enabled) { setPrints([]); return; }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const STEP = 38;      // distance between paw-prints, px
    const OFFSET = 9;     // left/right foot spread, px
    const LIFE = 2000;    // must match the CSS fade duration

    function onMove(e: PointerEvent) {
      const x = e.clientX;
      const y = e.clientY;
      const l = last.current;
      if (!l) { last.current = { x, y }; return; }
      const dx = x - l.x;
      const dy = y - l.y;
      const dist = Math.hypot(dx, dy);
      if (dist < STEP) return;

      // Toes point in the direction of travel (paw art faces "up" by default).
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      // Offset perpendicular to travel, alternating sides = left/right feet.
      const px = (-dy / dist) * OFFSET * foot.current;
      const py = (dx / dist) * OFFSET * foot.current;
      foot.current = foot.current === 1 ? -1 : 1;
      last.current = { x, y };

      const id = idRef.current++;
      setPrints((p) => [...p.slice(-36), { id, x: x + px, y: y + py, angle }]);
      window.setTimeout(() => setPrints((p) => p.filter((pr) => pr.id !== id)), LIFE);
    }

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [enabled]);

  return (
    <div className="paw-trail" aria-hidden="true">
      {prints.map((pr) => (
        <span key={pr.id} className="paw" style={{ left: pr.x, top: pr.y, transform: `translate(-50%,-50%) rotate(${pr.angle}deg)` }}>
          <span className="paw-inner">
            <svg viewBox="0 0 100 100">
              <ellipse cx="50" cy="70" rx="27" ry="23" />
              <ellipse cx="22" cy="42" rx="9" ry="13" />
              <ellipse cx="40" cy="27" rx="9.5" ry="14.5" />
              <ellipse cx="60" cy="27" rx="9.5" ry="14.5" />
              <ellipse cx="78" cy="42" rx="9" ry="13" />
            </svg>
          </span>
        </span>
      ))}
    </div>
  );
}
